/** Grilla inventario de gestión — campos homologados con modal (spec). */
import { Form } from 'react-bootstrap';
import { IoTrashOutline } from 'react-icons/io5';
import { LuPencilLine } from 'react-icons/lu';
import {
  fmtNum,
  mergeLineaPatch,
  recalcLinea,
  tipoNfLabel,
  cleanCnpj,
  sanitizeIntInput,
  sanitizeDecimalInput,
} from './inventarioCalculos';
import { ALL_GRID_FIELDS, ALL_GRID_FIELDS_GUARDADAS, CABECERA_PATCH_FIELDS, GESTION_PATCH_FIELDS, inputSanitizeMode, isNumericColumn, LINEA_PATCH_FIELDS } from './inventarioFieldConfig';
import {
  applyGestionNota,
  ESTADO_CERRADO,
  ESTADO_DISPONIBLE,
  ESTADO_EN_PROCESO,
  esEstadoAutomatico,
  resolverEstadoMercaderia,
  TIPOS_MOVIMIENTO_GESTION,
  tipoMovimientoLabel,
  opcionesTipoMovimiento,
} from './inventarioEstados';
import { applyGradoToLinea, opcionesAlmacen, preciosBrlPorGrado } from './inventarioOcUtils';
import DateInput from '../../components/DateInput/DateInput';

export function flattenNotasAFilas(notas) {
  const rows = [];
  for (const nota of notas || []) {
    const key = nota._key || `nf-${nota.id}`;
    const lineasActivas = nota.lineas || [];
    const idsActivas = new Set(lineasActivas.map((ln) => String(ln.id)));
    const lineas = [
      ...lineasActivas,
      ...(nota.lineas_retiradas || [])
        .filter((ln) => !idsActivas.has(String(ln.id)))
        .map((ln) => ({ ...ln, _retirada: true })),
    ];
    if (!lineas.length && !nota.id) lineas.push({});
    const usos = new Map(
      (nota.lineas_usadas || []).map((linea) => [String(linea.id_linea_origen), linea])
    );
    const saldos = new Map(
      (nota.saldos_liberados || []).map((linea) => [String(linea.id_linea_origen), linea])
    );

    lineas.forEach((ln, lineIdx) => {
      const lineaUsada = ln.id == null ? null : usos.get(String(ln.id));
      const saldoLiberado = ln.id == null ? null : saldos.get(String(ln.id));
      const filasComposicion = [];

      if (ln._retirada) {
        filasComposicion.push({
          linea: {
            ...ln,
            id_linea_origen: ln.id,
            paquetes: lineaUsada?.paquetes ?? null,
            pcs_paq: lineaUsada?.pcs_paq ?? null,
            total_pcs: lineaUsada?.total_pcs ?? null,
            total_m3: lineaUsada?.total_m3 ?? null,
            total_brl_m3: lineaUsada?.total_brl_m3 ?? null,
            total_usd_m3: lineaUsada?.total_usd_m3 ?? null,
            composicion_retirada: true,
            retirada_con_uso: !!lineaUsada,
            revision_pendiente: !!lineaUsada?.revision_pendiente || !!ln.revision_pendiente,
          },
          tipo: 'retirada',
          suffix: `retirada-${ln.id}`,
        });
      } else if (lineaUsada) {
        filasComposicion.push({
          linea: {
            ...ln,
            id_linea_origen: ln.id,
            paquetes: lineaUsada.paquetes,
            total_pcs: lineaUsada.total_pcs,
            total_m3: lineaUsada.total_m3,
            total_brl_m3: lineaUsada.total_brl_m3,
            total_usd_m3: lineaUsada.total_usd_m3,
          },
          tipo: 'utilizada',
          suffix: `usada-${ln.id}`,
        });
      }
      if (saldoLiberado) {
        filasComposicion.push({
          linea: {
            ...ln,
            id: saldoLiberado.id,
            id_linea_origen: ln.id,
            paquetes: saldoLiberado.paquetes,
            pcs_paq: saldoLiberado.pcs_paq,
            total_pcs: saldoLiberado.total_pcs,
            total_m3: saldoLiberado.total_m3,
            estado_mercaderia: saldoLiberado.estado_mercaderia,
            revision_pendiente: saldoLiberado.revision_pendiente,
            fecha_liberacion: saldoLiberado.fecha_liberacion,
          },
          tipo: saldoLiberado.tipo_fila_inventario || 'saldo_liberado',
          suffix: `saldo-${saldoLiberado.id}`,
        });
      }
      if (!ln._retirada && !lineaUsada && !saldoLiberado) {
        filasComposicion.push({ linea: ln, tipo: 'composicion', suffix: `linea-${ln.id ?? lineIdx}` });
      }

      for (const fila of filasComposicion) {
        const linea = fila.linea;
        const estadoLinea =
          fila.tipo === 'utilizada'
            ? ESTADO_EN_PROCESO
            : (fila.tipo === 'saldo_liberado' || fila.tipo === 'saldo_disponible')
              ? (linea.revision_pendiente ? ESTADO_EN_PROCESO : ESTADO_DISPONIBLE)
              : linea.estado_mercaderia || nota.estado_mercaderia || '';
        rows.push({
          _rowKey: `${key}-${fila.suffix}`,
          _notaKey: key,
          _lineIdx: fila.tipo === 'composicion' ? lineIdx : -1,
          _sourceLineIdx: lineIdx,
          _tipoFilaInventario: fila.tipo,
          _activa: linea.activa !== false && fila.tipo !== 'retirada',
          _esFilaDerivada: fila.tipo !== 'composicion',
          _notaId: nota.id,
          id_producto: nota.id_producto ?? '',
          _lineaId: linea.id_linea_origen ?? linea.id ?? null,
          _lineaVisualId: linea.id_linea_origen ?? linea.id ?? null,
          _lineaIds:
            (linea.id_linea_origen ?? linea.id) == null
              ? []
              : [Number(linea.id_linea_origen ?? linea.id)].filter(
                  (id) => Number.isFinite(id) && id > 0
                ),
          _guardada: !!nota.id && String(nota._key || '').startsWith('guardada-'),
          archivo_origen: nota.archivo_origen ?? '',
          cnpj_match: nota.cnpj_match !== false,
          calidades_oc: nota.calidades_oc || [],
          _dimEsp: nota._dimEsp || [],
          _dimAnc: nota._dimAnc || [],
          _dimLar: nota._dimLar || [],
          tipo_nf: nota.tipo_nf ?? '',
          cnpj: cleanCnpj(nota.cnpj ?? ''),
          id_proveedor: nota.id_proveedor ?? '',
          id_ordencompra: nota.id_ordencompra ?? '',
          proveedor: nota.nombre_proveedor ?? nota.proveedor ?? '',
          producto_nombre: nota.producto_nombre ?? '',
          tipo_movimiento: nota.tipo_movimiento ?? '',
          fecha_mov_almacen: nota.fecha_mov_almacen ?? '',
          estado_mercaderia: estadoLinea,
          revision_pendiente: !!linea.revision_pendiente,
          fecha_ingreso_puerto: nota.fecha_ingreso_puerto ?? '',
          oc_label: nota.oc_label ?? '',
          almacen: nota.almacen ?? '',
          booking: nota.booking ?? '',
          nro_nf: nota.nro_nf ?? '',
          incoterm_id: nota.incoterm_id ?? '',
          incoterm_label: nota.incoterm_label ?? '',
          categoria: nota.categoria ?? '',
          especie: nota.especie ?? '',
          fecha_nf: nota.fecha_nf ?? '',
          valor_total_nf: nota.valor_total_nf ?? '',
          valor_total_prod: nota.valor_total_prod ?? '',
          peso_liquido: nota.peso_liquido ?? '',
          peso_bruto: nota.peso_bruto ?? '',
          tc_bcb: nota.tc_bcb ?? '',
          paquetes: linea.paquetes ?? '',
          pcs_paq: linea.pcs_paq ?? '',
          total_pcs: linea.total_pcs ?? '',
          altura_rw: linea.altura_rw ?? '',
          castigo_rw: linea.castigo_rw ?? '',
          grado: linea.grado ?? '',
          espesor: linea.espesor ?? '',
          ancho: linea.ancho ?? '',
          largo: linea.largo ?? '',
          total_m3: linea.total_m3 ?? '',
          precio_brl_unit_m3: linea.precio_brl_unit_m3 ?? '',
          total_brl_m3: linea.total_brl_m3 ?? '',
          tc_calculado: linea.tc_calculado ?? '',
          diferencia: linea.diferencia ?? '',
          precio_usd_unit_m3: linea.precio_usd_unit_m3 ?? '',
          total_usd_m3: linea.total_usd_m3 ?? '',
        });
      }
    });
  }
  const consolidated = [];
  const saldosByComposition = new Map();
  const normalize = (value) => {
    if (value == null || value === '') return '';
    let raw = String(value).trim();
    if (raw.includes(',') && raw.includes('.')) {
      raw = raw.lastIndexOf(',') > raw.lastIndexOf('.')
        ? raw.replace(/\./g, '').replace(',', '.')
        : raw.replace(/,/g, '');
    } else if (raw.includes(',')) {
      raw = raw.replace(',', '.');
    } else if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
      raw = raw.replace(/\./g, '');
    }
    const number = Number(raw);
    return Number.isFinite(number) ? String(number) : String(value).trim().toLowerCase();
  };
  for (const row of rows) {
    if (!['saldo_liberado', 'saldo_disponible'].includes(row._tipoFilaInventario)) {
      consolidated.push(row);
      continue;
    }
    const composition = [
      row._notaId,
      row.id_producto,
      normalize(row.grado),
      normalize(row.espesor),
      normalize(row.ancho),
      normalize(row.largo),
      normalize(row.altura_rw),
      normalize(row.castigo_rw),
      normalize(row.pcs_paq),
      row.revision_pendiente ? 'revision' : 'normal',
    ].join('|');
    const existing = saldosByComposition.get(composition);
    if (!existing) {
      const grouped = { ...row, _lineaIds: [...new Set(row._lineaIds || [])] };
      saldosByComposition.set(composition, grouped);
      consolidated.push(grouped);
      continue;
    }
    existing._lineaIds = [...new Set([...(existing._lineaIds || []), ...(row._lineaIds || [])])];
    if (row._tipoFilaInventario === 'saldo_liberado') {
      existing._tipoFilaInventario = 'saldo_liberado';
    }
    const sumKnown = (left, right) => (
      left == null || left === '' || right == null || right === ''
        ? null
        : (Number(left) || 0) + (Number(right) || 0)
    );
    existing.paquetes = sumKnown(existing.paquetes, row.paquetes);
    existing.total_pcs = sumKnown(existing.total_pcs, row.total_pcs);
    existing.total_m3 = sumKnown(existing.total_m3, row.total_m3);
    existing.total_brl_m3 = sumKnown(existing.total_brl_m3, row.total_brl_m3);
    existing.total_usd_m3 = sumKnown(existing.total_usd_m3, row.total_usd_m3);
    existing._rowKey += `+${row._rowKey}`;
    const fechasLiberacion = [existing.fecha_liberacion, row.fecha_liberacion]
      .filter(Boolean)
      .sort();
    if (fechasLiberacion.length) {
      existing.fecha_liberacion = fechasLiberacion[fechasLiberacion.length - 1];
    }
    if (normalize(existing.precio_brl_unit_m3) !== normalize(row.precio_brl_unit_m3)) {
      existing.precio_brl_unit_m3 = '';
    }
    if (normalize(existing.precio_usd_unit_m3) !== normalize(row.precio_usd_unit_m3)) {
      existing.precio_usd_unit_m3 = '';
    }
  }
  return consolidated;
}

/**
 * Ordena filas de detalle conservando el orden de las NF padre y, dentro de
 * cada NF, el orden de sus líneas hijas. No agrega filas resumen de la NF.
 */
export function ordenarFilasPorPadreHijo(rows) {
  const parentOrder = new Map();
  const source = Array.isArray(rows) ? rows : [];
  source.forEach((row, index) => {
    const key = row?._notaKey ?? `row-${index}`;
    if (!parentOrder.has(key)) parentOrder.set(key, index);
  });
  return source
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const aParent = parentOrder.get(a.row?._notaKey) ?? a.index;
      const bParent = parentOrder.get(b.row?._notaKey) ?? b.index;
      if (aParent !== bParent) return aParent - bParent;
      const aSourceLine = a.row?._sourceLineIdx ?? a.row?._lineIdx;
      const bSourceLine = b.row?._sourceLineIdx ?? b.row?._lineIdx;
      const aLine = Number.isFinite(Number(aSourceLine)) ? Number(aSourceLine) : a.index;
      const bLine = Number.isFinite(Number(bSourceLine)) ? Number(bSourceLine) : b.index;
      return aLine - bLine || a.index - b.index;
    })
    .map(({ row }) => row);
}

export function patchFilaEnNota(nota, lineIdx, field, value, { incoterms } = {}) {
  const spec = ALL_GRID_FIELDS.find((f) => f.field === field);
  const mode = inputSanitizeMode(spec);
  let v = value;
  if (mode === 'digits' || mode === 'int') v = sanitizeIntInput(value);
  else if (mode === 'decimal') v = sanitizeDecimalInput(value);

  if (LINEA_PATCH_FIELDS.has(field)) {
    const medidas = nota._medidasPorGrado || {};
    const usd = nota._usdPorGrado || {};
    const brl = nota._brlPorGrado || preciosBrlPorGrado(nota.lineas);
    const lineas = (nota.lineas || []).map((ln, i) => {
      if (i !== lineIdx) return ln;
      let updated = mergeLineaPatch(ln, field === 'grado' ? { grado: v } : { [field]: v });
      if (field === 'grado') {
        updated = applyGradoToLinea(updated, v, medidas, usd, brl);
      }
      return recalcLinea(updated, nota.tc_bcb);
    });
    return { ...nota, lineas };
  }
  if (field === 'incoterm_id') {
    const inc = (incoterms || []).find((i) => String(i.id_incoterm) === String(v));
    return {
      ...nota,
      incoterm_id: v ? Number(v) : null,
      incoterm_label: inc?.nombre || '',
    };
  }
  if (GESTION_PATCH_FIELDS.has(field)) {
    return applyGestionNota({ ...nota, [field]: v }, { [field]: v }, { incoterms });
  }
  if (CABECERA_PATCH_FIELDS.has(field)) {
    const next = { ...nota, [field]: v };
    if (field === 'tc_bcb') {
      return {
        ...next,
        lineas: (next.lineas || []).map((ln) => recalcLinea(ln, v)),
      };
    }
    return next;
  }
  return nota;
}

function cellText(v, dec) {
  if (v == null || v === '') return '—';
  if (dec != null) return fmtNum(v, dec);
  return String(v);
}

function formatDateDisplay(v) {
  if (v == null || v === '') return '—';
  const s = String(v).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s || '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function fieldWrap(node, numeric = false) {
  const inner = numeric ? <div className="inv-grid-num-cell">{node}</div> : node;
  return <div className="inv-grid-cell">{inner}</div>;
}

function sanitizeInputValue(mode, v) {
  if (mode === 'digits' || mode === 'int') return sanitizeIntInput(v);
  if (mode === 'decimal') return sanitizeDecimalInput(v);
  return v;
}

function inp(value, onChange, { numeric = false, sanitize = null, type = 'text' } = {}) {
  if (type === 'date') {
    return (
      <DateInput
        size="sm"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="inv-grid-input"
      />
    );
  }
  const inputMode = sanitize === 'decimal' ? 'decimal' : sanitize ? 'numeric' : undefined;
  return (
    <Form.Control
      size="sm"
      type={type}
      inputMode={inputMode}
      value={value ?? ''}
      onChange={(e) => onChange(sanitize ? sanitizeInputValue(sanitize, e.target.value) : e.target.value)}
      className={`inv-grid-input${numeric ? ' inv-grid-num' : ''}`}
    />
  );
}

function sel(value, onChange, options, empty = '—') {
  const opts = options?.length ? options : value ? [value] : [];
  return (
    <Form.Select size="sm" value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="inv-grid-input">
      <option value="">{empty}</option>
      {opts.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </Form.Select>
  );
}

function renderCell(row, spec, { editable, editableGestion, onPatch, onEdit, almacenes, puertos, incoterms, proveedores }) {
  const { field, type, decimals } = spec;
  const numeric = isNumericColumn(spec);
  const wrap = (node) => fieldWrap(node, numeric);
  const ch = (v) => onPatch?.(row, field, v);
  const gestionEditable = editableGestion && onPatch && row._lineIdx === 0 && !row._isNotaDetail;
  const notaRow = row;

  const renderGestionReadonly = () => {
    if (type === 'tipo_movimiento') return fieldWrap(tipoMovimientoLabel(notaRow.tipo_movimiento));
    if (type === 'fecha_movimiento') {
      return fieldWrap(formatDateDisplay(notaRow.fecha_mov_almacen));
    }
    if (type === 'disponibilidad_producto') return fieldWrap(resolverEstadoMercaderia(notaRow));
    if (type === 'date' && field === 'fecha_ingreso_puerto') {
      return fieldWrap(formatDateDisplay(notaRow.fecha_ingreso_puerto));
    }
    return fieldWrap(cellText(notaRow[field], decimals));
  };

  if (
    type === 'tipo_movimiento' ||
    type === 'disponibilidad_producto' ||
    type === 'fecha_movimiento' ||
    field === 'fecha_ingreso_puerto'
  ) {
    if (!gestionEditable || type === 'fecha_movimiento') return renderGestionReadonly();
    if (type === 'tipo_movimiento') {
      const opts = opcionesTipoMovimiento(notaRow, incoterms);
      return wrap(
        <Form.Select
          size="sm"
          className="inv-grid-input"
          value={notaRow.tipo_movimiento ?? ''}
          onChange={(e) => ch(e.target.value)}
        >
          {opts.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Form.Select>
      );
    }
    if (type === 'disponibilidad_producto') {
      const estado = resolverEstadoMercaderia(notaRow);
      if (esEstadoAutomatico(notaRow)) {
        return fieldWrap(<span className="small text-muted">{estado}</span>);
      }
      return wrap(
        <Form.Select
          size="sm"
          className="inv-grid-input"
          value={estado}
          onChange={(e) => ch(e.target.value)}
        >
          <option value={ESTADO_DISPONIBLE}>{ESTADO_DISPONIBLE}</option>
          <option value={ESTADO_EN_PROCESO}>{ESTADO_EN_PROCESO}</option>
          <option value={ESTADO_CERRADO}>{ESTADO_CERRADO}</option>
        </Form.Select>
      );
    }
    if (field === 'fecha_ingreso_puerto') {
      return wrap(inp((notaRow.fecha_ingreso_puerto || '').slice(0, 10), ch, { type: 'date' }));
    }
  }

  if (!editable || !onPatch) {
    if (field === 'tipo_nf') return wrap(cellText(tipoNfLabel(row.tipo_nf)));
    if (field === 'incoterm_id') return wrap(cellText(row.incoterm_label || '—'));
    if (type === 'calc' || type === 'readonly') return wrap(cellText(row[field], decimals));
    if (field === 'fecha_nf') return fieldWrap(formatDateDisplay(row.fecha_nf));
    if (
      field === 'nro_nf' &&
      onEdit &&
      (!row._esFilaDerivada || row._guardada)
    ) {
      return fieldWrap(
        <button
          type="button"
          className="btn btn-link p-0 text-primary inv-nf-link"
          title={`Editar NF ${row.nro_nf || ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(row);
          }}
        >
          {cellText(row.nro_nf)}
        </button>
      );
    }
    if (numeric) return wrap(cellText(row[field], decimals));
    return fieldWrap(cellText(row[field], decimals));
  }

  switch (type) {
    case 'tipo_nf':
      return wrap(
        <Form.Select size="sm" value={row.tipo_nf ?? ''} onChange={(e) => ch(e.target.value)} className="inv-grid-input">
          <option value="0">Entrada</option>
          <option value="1">Salida</option>
        </Form.Select>
      );
    case 'readonly':
      return fieldWrap(cellText(row[field]));
    case 'almacen': {
      const almOpts = opcionesAlmacen({
        incotermId: row.incoterm_id,
        incoterms,
        idProveedor: row.id_proveedor,
        cnpj: row.cnpj,
        proveedores,
        almacenes,
        puertos,
      });
      if (almOpts.opciones.length > 0) return wrap(sel(row.almacen, ch, almOpts.opciones));
      return wrap(inp(row.almacen, ch));
    }
    case 'incoterm':
      return fieldWrap(
        <Form.Select
          size="sm"
          value={row.incoterm_id ?? ''}
          onChange={(e) => onPatch(row, 'incoterm_id', e.target.value)}
          className="inv-grid-input inv-grid-incoterm"
        >
          <option value="">—</option>
          {(incoterms || []).map((i) => (
            <option key={i.id_incoterm} value={i.id_incoterm}>
              {i.nombre}
            </option>
          ))}
        </Form.Select>
      );
    case 'date':
      return wrap(inp((row[field] || '').slice(0, 10), ch, { type: 'date' }));
    case 'grado':
      return wrap(sel(row.grado, ch, row.calidades_oc));
    case 'dim':
      return wrap(inp(row[field], ch, { numeric: true, sanitize: 'decimal' }));
    case 'calc':
      return wrap(cellText(row[field], decimals));
    case 'decimal_editable':
    case 'decimal':
    case 'number':
    case 'int':
    case 'text': {
      const sanitize = inputSanitizeMode(spec);
      if (sanitize) {
        return wrap(inp(row[field], ch, { numeric: true, sanitize }));
      }
      return wrap(inp(row[field], ch));
    }
    default:
      return fieldWrap(cellText(row[field]));
  }
}

export const INVENTARIO_TABLE_STYLES = {
  headCells: {
    style: {
      paddingLeft: '5px',
      paddingRight: '5px',
      fontSize: '0.75rem',
    },
  },
  cells: {
    style: {
      paddingLeft: '5px',
      paddingRight: '5px',
      fontSize: '0.8rem',
    },
  },
};

export function buildColumnasInventario({
  editable,
  editableGestion = false,
  includeGestion = false,
  onPatch,
  onEdit,
  onQuitar,
  almacenes,
  puertos,
  incoterms,
  proveedores,
}) {
  const ctx = { editable, editableGestion, onPatch, onEdit, almacenes, puertos, incoterms, proveedores };
  const fields = includeGestion ? ALL_GRID_FIELDS_GUARDADAS : ALL_GRID_FIELDS;
  const cols = fields.map((spec) => ({
    name: spec.label,
    width: spec.width,
    minWidth: spec.width,
    maxWidth: spec.width,
    grow: 0,
    shrink: 0,
    wrap: false,
    center: isNumericColumn(spec),
    cell: (row) => renderCell(row, spec, ctx),
    selector: (row) => row[spec.field],
  }));

  if (onEdit || onQuitar) {
    cols.push({
      name: '',
      width: '90px',
      ignoreRowClick: true,
      cell: (row) => (
        <div className="d-flex flex-nowrap gap-1">
          {onEdit && row._lineIdx === 0 && !row._isNotaDetail ? (
            <button type="button" className="btn btn-outline-primary btn-sm" title="Editar NF" onClick={() => onEdit(row)}>
              <LuPencilLine />
            </button>
          ) : null}
          {onQuitar && row._lineIdx === 0 && !row._guardada ? (
            <button type="button" className="btn btn-outline-danger btn-sm" title="Quitar NF" onClick={() => onQuitar(row)}>
              <IoTrashOutline />
            </button>
          ) : null}
        </div>
      ),
    });
  }

  return cols;
}
